import * as React from 'react';
import { useState } from 'react';
import type {
  Control,
  FieldValues,
  Path,
  RegisterOptions,
} from 'react-hook-form';
import { useController } from 'react-hook-form';
import type {
  NativeSyntheticEvent,
  TextInput,
  TextInputFocusEventData,
  TextInputProps,
} from 'react-native';
import { I18nManager, Keyboard, Pressable, View } from 'react-native';
import { TextInput as NTextInput } from 'react-native';
import { useSharedValue, withSpring } from 'react-native-reanimated';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { Text } from '@/ui/text';

import { Icon } from '../icon';
import { Cancel } from '../icons/cancel';
import { Eye } from '../icons/eye';
import { EyeOff } from '../icons/eye-off';
import { Tooltip } from '../tooltip';

export interface NInputProps extends TextInputProps {
  label: string;
  disabled?: boolean;
  error?: string;
  infoText?: string;
  containerStyle?: any;
  inputStyle?: any;
  type?: 'text';
  onChangeText?: (text: string) => void;
  onBlur?: (e: NativeSyntheticEvent<TextInputFocusEventData>) => void;
  rightIcon?: React.ReactNode;
  onRightIconPress?: () => void;
  textContainerStyle?: any;
  onFocus?: () => void;
  tooltipText?: string;
  leftIcon?: React.ReactNode;
  nativeInputRef?: React.RefObject<TextInput | null>;
}

type TRule = Omit<
  RegisterOptions,
  'valueAsNumber' | 'valueAsDate' | 'setValueAs'
>;

export type RuleType<T> = { [name in keyof T]: TRule };
export type InputControllerType<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  rules?: TRule;
};

interface ControlledInputProps<T extends FieldValues>
  extends NInputProps,
    InputControllerType<T> {}

export const Input = React.forwardRef<TextInput, NInputProps>((props, ref) => {
  const { styles, theme } = useStyles(inputStyleSheet);
  const {
    label,
    error,
    disabled,
    infoText,
    onChangeText,
    onBlur,
    secureTextEntry,
    rightIcon,
    onRightIconPress,
    onFocus,
    tooltipText,
    leftIcon,
    ...inputProps
  } = props;
  const [isFocused, setIsFocused] = React.useState(false);
  const [text, setText] = React.useState('');
  const [containerHeight, setContainerHeight] = React.useState(0);
  const [secureText, setSecureText] = React.useState(secureTextEntry);
  const [state, setState] = useState<
    'default' | 'filled' | 'active' | 'error' | 'disabled'
  >('default');

  React.useEffect(() => {
    setText(inputProps.value || '');
    // if (inputProps.value) {
    // }
  }, [inputProps.value]);

  React.useEffect(() => {
    const keyboardDidHideListener = Keyboard.addListener(
      'keyboardDidHide',
      () => {
        setIsFocused(false);
      }
    );

    return () => {
      keyboardDidHideListener.remove();
    };
  }, []);

  React.useEffect(() => {
    if (error) {
      setState('error');
      return;
    }

    if (isFocused) {
      setState('active');
      return;
    }

    if (disabled) {
      setState('disabled');
      return;
    }

    if (!isFocused && text.length > 0) {
      setState('filled');
      return;
    }

    setState('default');
  }, [text, isFocused, error, disabled]);

  const handleBlur = React.useCallback(() => {
    setIsFocused(false);
  }, []);

  const handleFocus = React.useCallback(() => {
    onFocus?.();
    setIsFocused(true);
  }, []);

  const translateY = useSharedValue(0);

  // console.log('label', label, state, isFocused, text);

  React.useEffect(() => {
    if (isFocused || text.length > 0) {
      const percentage = infoText || error ? 0.3 : 0.47;
      const percentageOfHeight = containerHeight * percentage;

      translateY.value = withSpring(-percentageOfHeight, { duration: 2000 });
    } else if (text.length === 0) {
      translateY.value = withSpring(0, { duration: 1000 });
    }
  }, [isFocused, containerHeight, text, error, infoText, translateY]);

  const renderFooter = React.useCallback(() => {
    if (error) {
      return (
        <View>
          <Text style={styles[`${state}HelperText`]}>{error}</Text>
        </View>
      );
    }

    if (infoText) {
      return (
        <View>
          <Text style={styles[`${state}HelperText`]}>{infoText}</Text>
        </View>
      );
    }

    return null;
  }, [error, infoText, state, styles]);

  return (
    <View
      style={[styles.container, inputProps.containerStyle]}
      onLayout={(event) => {
        const { height } = event.nativeEvent.layout;
        setContainerHeight(height);
      }}
    >
      {label ? (
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Text
            type="caption1Bold"
            style={[styles.label, styles[`${state}Text`]]}
          >
            {label}
          </Text>

          {tooltipText ? <Tooltip text={tooltipText} /> : null}
        </View>
      ) : null}

      <View
        style={[
          styles.textContainer,
          styles[`${state}`],
          inputProps.textContainerStyle,
        ]}
      >
        {leftIcon ? <View style={{ paddingLeft: 16 }}>{leftIcon}</View> : null}

        <NTextInput
          ref={ref}
          placeholderTextColor={theme.colors.text.secondary}
          onBlur={(e: NativeSyntheticEvent<TextInputFocusEventData>) => {
            onBlur && onBlur(e);
            handleBlur();
          }}
          onFocus={handleFocus}
          onChangeText={(t: string) => {
            onChangeText && onChangeText(t);
            setText(t);
          }}
          style={[
            {
              writingDirection: I18nManager.isRTL ? 'rtl' : 'ltr',
              outline: 'none',
            },
            styles.input,
            styles[`${state}Text`],
            inputProps.inputStyle,
          ]}
          secureTextEntry={secureText}
          editable={!disabled}
          pointerEvents={disabled ? 'none' : 'auto'}
          autoCapitalize="none"
          {...inputProps}
        />

        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
          {rightIcon ? (
            <Pressable onPress={onRightIconPress}>{rightIcon}</Pressable>
          ) : null}

          {secureTextEntry && secureText && (
            <Pressable onPress={() => setSecureText(!secureText)}>
              <Icon
                type="naked"
                icon={<EyeOff />}
                fillColor={theme.colors.icon.nakedColor}
                width={18}
                height={18}
              />
            </Pressable>
          )}

          {secureTextEntry && !secureText && (
            <Pressable onPress={() => setSecureText(!secureText)}>
              <Icon
                type="naked"
                icon={<Eye />}
                fillColor={theme.colors.icon.nakedColor}
                width={18}
                height={18}
              />
            </Pressable>
          )}

          {!disabled && text !== '' && !rightIcon && (
            <Pressable
              hitSlop={8}
              onPress={() => {
                setText('');
                onChangeText?.('');
              }}
              style={({ pressed }) => [{ opacity: pressed ? 0.5 : 1 }]}
            >
              <Icon
                type="naked"
                icon={<Cancel />}
                stroke={theme.colors.navigationButton.color}
                fillColor={theme.colors.navigationButton.color}
                width={12}
                height={12}
              />
            </Pressable>
          )}
        </View>
      </View>

      {renderFooter()}
    </View>
  );
});

const inputStyleSheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    flexShrink: 1,
    gap: 8,
  },
  default: {
    borderColor: theme.colors.input.default.border,
  },
  defaultText: {
    color: theme.colors.input.default.text,
  },
  defaultHelperText: {
    color: theme.colors.input.default.helperText,
  },
  filled: {
    borderColor: theme.colors.input.filled.border,
  },
  filledText: {
    color: theme.colors.input.filled.text,
  },
  filledHelperText: {
    color: theme.colors.input.filled.helperText,
  },
  active: {
    borderColor: theme.colors.input.active.border,
  },
  activeText: {
    color: theme.colors.input.active.text,
  },
  activeHelperText: {
    color: theme.colors.input.active.helperText,
  },
  error: {
    borderColor: theme.colors.input.error.border,
  },
  errorText: {
    color: theme.colors.input.error.text,
  },
  errorHelperText: {
    color: theme.colors.input.error.helperText,
  },
  disabled: {
    backgroundColor: theme.colors.input.disabled.fill,
    borderColor: theme.colors.input.disabled.border,
  },
  disabledText: {
    color: theme.colors.input.disabled.text,
  },
  disabledHelperText: {
    color: theme.colors.input.disabled.helperText,
  },
  textContainer: {
    width: '100%',
    height: 50,
    borderWidth: 1,
    gap: 8,
    borderRadius: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    paddingRight: 16,
    paddingLeft: 8,
  },
  label: {
    paddingLeft: 4,
    paddingRight: 4,
  },
  input: {
    flex: 1,
    minWidth: 0,
    fontSize: 16,
    height: '100%',
    paddingLeft: 8,
  },
}));

// only used with react-hook-form
export function ControlledInput<T extends FieldValues>(
  props: ControlledInputProps<T>
) {
  const { name, control, rules, ...inputProps } = props;

  const { field, fieldState } = useController({ control, name, rules });
  return (
    <Input
      ref={field.ref}
      autoCapitalize="none"
      onChangeText={field.onChange}
      value={(field.value as string) || ''}
      {...inputProps}
      error={fieldState.error?.message}
    />
  );
}
